import { Form, Input, Select } from "../../../../../../shared/frontend/ui";
import { useFormDirty } from "../../../../../../shared/frontend/forms/useFormDirty";
import React from "react";
import { useAuth } from "../../app/auth/useAuth";
import { LEAD_PIPELINE_KIND_MEMBER_CARE } from "../../app/constants/leadContact";
import { formDefaults } from "../../app/design-system";
import { useI18n } from "../../app/i18n/useI18n";
import { useGlobalStudioContext } from "../../app/studios/useGlobalStudioContext";
import { INTERNAL_FORM_AUTOCOMPLETE } from "../../app/utils/browserAutofill";
import { LIFECYCLE_MEMBER } from "../../pages/persons/lifecycle";
import { FormDrawer } from "../FormDrawer";
import { useFeedback } from "../../../../../../shared/frontend/feedback/useFeedback";

const PERSON_SEARCH_MIN_LENGTH = 2;
const PERSON_SEARCH_LIMIT = 20;

export type MemberCarePipelineOption = { id: string; name: string; isActive?: boolean; kind?: number };

type Props = {
  open: boolean;
  contentLoading?: boolean;
  onCancel: () => void;
  /** Nach dem Anlegen mit der Kennung des neuen Vorgangs. */
  onCreated: (leadId: string) => void;
  pipelines: MemberCarePipelineOption[];
  accounts: { id: string; name: string }[];
  /** Vorbelegte Person (Personenakte); ohne Angabe wird gesucht. */
  personId?: string | null;
  personName?: string | null;
  /** Vorbelegtes Vertragspaket (Rückgewinnung, Verlängerung). */
  contractPackageId?: string | null;
};

type FormValues = { personId: string; pipelineId: string; assignedAccountId?: string | null; notes?: string | null };
type PersonOption = { id: string; firstName?: string | null; lastName?: string | null };

/**
 * Betreuungsvorgang für ein bestehendes Mitglied anlegen — aus der Vorgangsliste
 * (mit Mitgliedersuche) und aus der Personenakte (Person vorbelegt). Ein Vorgang, ein Anlegepfad:
 * `POST /api/v1/leads/care`; die Regeln (nur Mitglieder, ein offener je Pipeline, Mindestabstand)
 * prüft das Backend und antwortet mit einem sprechenden Schlüssel.
 */
export function MemberCareCaseCreateDrawer({ open, contentLoading, onCancel, onCreated, pipelines, accounts, personId, personName, contractPackageId }: Props) {
  const auth = useAuth();
  const { text } = useI18n();
  const feedback = useFeedback();
  const { appendStudioIdsQuery } = useGlobalStudioContext();
  const [form] = Form.useForm<FormValues>();
  const { dirty, initialize } = useFormDirty(form);
  const [saving, setSaving] = React.useState(false);
  const [personOptions, setPersonOptions] = React.useState<PersonOption[]>([]);
  const [personSearching, setPersonSearching] = React.useState(false);
  const formId = "member-care-case-create-form";

  const pipelineOptions = React.useMemo(
    () => pipelines
      .filter((p) => p.isActive !== false && (p.kind ?? LEAD_PIPELINE_KIND_MEMBER_CARE) === LEAD_PIPELINE_KIND_MEMBER_CARE)
      .map((p) => ({ value: p.id, label: p.name })),
    [pipelines]
  );

  React.useEffect(() => {
    if (!open) return;
    initialize({
      personId: personId ?? undefined,
      pipelineId: pipelineOptions.length === 1 ? pipelineOptions[0]!.value : undefined,
      assignedAccountId: null,
      notes: null
    });
  }, [open, personId, pipelineOptions, form, initialize]);

  const searchPersons = React.useCallback(
    async (q: string) => {
      const t = (q || "").trim();
      if (t.length < PERSON_SEARCH_MIN_LENGTH) {
        setPersonOptions([]);
        return;
      }
      setPersonSearching(true);
      try {
        const params = new URLSearchParams();
        params.set("q", t);
        params.set("lifecycle", LIFECYCLE_MEMBER);
        const list = (await auth.api.get<PersonOption[]>(appendStudioIdsQuery(`/api/v1/persons?${params.toString()}`))) || [];
        setPersonOptions(list.slice(0, PERSON_SEARCH_LIMIT));
      } finally {
        setPersonSearching(false);
      }
    },
    [auth.api, appendStudioIdsQuery]
  );

  const submit = async (v: FormValues) => {
    setSaving(true);
    try {
      const created = await auth.api.post<{ id: string }>("/api/v1/leads/care", {
        personId: v.personId,
        pipelineId: v.pipelineId,
        contractPackageId: contractPackageId ?? null,
        assignedAccountId: v.assignedAccountId || null,
        notes: (v.notes || "").trim() || null
      });
      feedback.success(text("Saved."));
      onCreated(created.id);
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormDrawer mode="edit"
      contentLoading={contentLoading}
      title="New member care case"
      dirty={dirty}
      open={open}
      onCancel={onCancel}
      formId={formId}
      submitting={saving}
    >
      <Form form={form} id={formId} layout="vertical" requiredMark={formDefaults.requiredMark} onFinish={submit} autoComplete={INTERNAL_FORM_AUTOCOMPLETE}>
        <Form.Item name="personId" label={text("Member")} rules={[{ required: true, message: text("Please select a member") }]}>
          {personId ? (
            <Select disabled options={[{ value: personId, label: personName ?? personId }]} />
          ) : (
            <Select
              showSearch
              filterOption={false}
              onSearch={(q) => { void searchPersons(q); }}
              loading={personSearching}
              notFoundContent={personSearching ? null : text("Type at least 2 characters")}
              placeholder={text("Search member")}
              options={personOptions.map((p) => ({ value: p.id, label: [p.firstName, p.lastName].filter(Boolean).join(" ") || p.id }))}
            />
          )}
        </Form.Item>
        <Form.Item name="pipelineId" label={text("Pipeline")} rules={[{ required: true, message: text("Please select a pipeline") }]}>
          <Select options={pipelineOptions} placeholder={text("Select pipeline")} />
        </Form.Item>
        <Form.Item name="assignedAccountId" label={text("Assigned")}>
          <Select allowClear options={accounts.map((a) => ({ value: a.id, label: a.name }))} placeholder={text("Not assigned")} />
        </Form.Item>
        <Form.Item name="notes" label={text("Note")}>
          <Input.TextArea rows={3} />
        </Form.Item>
      </Form>
    </FormDrawer>
  );
}
